/* eslint-disable react/no-danger */
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import cx from 'classnames';
import { CloudinaryPicture, Button } from '@bwoty-web/ui-kit';
import {
  Link,
  Icon,
  USER_INTERFACE_TICK_DONE,
  TIME_LOCATION_MUSEUM,
  FOOD_BEVERAGE_BREAKFAST,
  FOOD_BEVERAGE_RESTAURANT,
  FOOD_BEVERAGE_GLASS,
  FOOD_BEVERAGE_MEAL_PACKAGE,
  MISC_FAMILY_FRIENDLY,
  MISC_FLAG_SCANDINAVIA,
  MISC_FLAG_FI,
  TRAVEL_MOUNTAIN,
  ACTIVITY_WATER,
  TRAVEL_SMALL_BOAT,
  ACTIVITY_SUN_BEDS,
  ACTIVITY_WALK_HIKE,
  ACTIVITY_DIVING,
  ACTIVITY_ISLAND,
  ACTIVITY_SAILBOAT,
  ACTIVITY_THEMEPARK,
  MISC_SPEED,
  EsgLogo,
  ArrowLink,
} from '@bwoty-web/styleguide';
import Subtitle from '../../common/subtitle';
import { shouldShowRegion } from '../../utils/shouldShowRegion';
import './excursionInfoView.scss';

const ExcursionInfoView = ({ excursion, resort, labels, siteId, isAppWebView = false }) => {
  const isTjareborg = siteId && parseInt(siteId, 10) === 15;
  const priceTextArray = [];
  if (isTjareborg) {
    if (excursion.priceAdultFI) { priceTextArray.push(`${excursion.priceAdultFI} ${excursion.currency} ${labels.perAdultLabel}`); }
    if (excursion.priceChildFI) { priceTextArray.push(`${excursion.priceChildFI} ${excursion.currency} ${labels.perChildLabel}`); }
  } else {
    if (excursion.priceAdult) { priceTextArray.push(`${excursion.priceAdult} ${excursion.currency} ${labels.perAdultLabel}`); }
    if (excursion.priceChild) { priceTextArray.push(`${excursion.priceChild} ${excursion.currency} ${labels.perChildLabel}`); }
  }
  const priceText = priceTextArray.length > 0 ? `${priceTextArray.join(', ')}.` : '';

  const excursionFacts = [];
  if (isTjareborg) {
    if (excursion.dayFI) { excursionFacts.push({ heading: labels.dayLabel, text: excursion.dayFI }); }
  } else if (excursion.day) {
    excursionFacts.push({ heading: labels.dayLabel, text: excursion.day });
  }

  if (excursion.timeDuration) { excursionFacts.push({ heading: labels.durationLabel, text: excursion.timeDuration }); }
  if (excursion.includedInPrice || (isTjareborg && excursion.priceAdultFI) || (isTjareborg && excursion.priceChildFI)
    || (!isTjareborg && excursion.priceAdult) || (!isTjareborg && excursion.priceChild)) {
    excursionFacts.push({ heading: labels.priceLabel, text: priceText, moreText: excursion.includedInPrice });
  }

  // if there is a finish guide => dont show there is a scandinavian guide as well, and show guideFI text instead of guide
  let uspListFiltered = excursion.uspList;
  if (excursion.uspList?.length > 0 && excursion.uspList?.some((usp) => usp.key === 'GUIDE_FI')) {
    uspListFiltered = excursion.uspList.filter((usp) => usp.key !== 'GUIDE_SCANDINAVIAN');
  }

  if (isTjareborg) {
    if (excursion.guideFI) { excursionFacts.push({ heading: labels.guideLabel, text: excursion.guideFI }); }
  } else if (excursion.guide) {
    excursionFacts.push({ heading: labels.guideLabel, text: excursion.guide });
  }

  if (excursion.ageRestriction) {
    excursionFacts.push({ heading: labels.ageRestrictionLabel, text: (labels.ageRestrictionText).replace('{ageRestriction}', excursion.ageRestriction) });
  }
  if (excursion.goodToBring) { excursionFacts.push({ heading: labels.goodToBringLabel, text: excursion.goodToBring }); }
  if (excursion.additionalInfo) { excursionFacts.push({ heading: labels.otherLabel, text: excursion.additionalInfo }); }
  if (excursion.preBookable) {
    excursionFacts.push({ heading: labels.bookableTrueLabel, text: labels.bookableTrueText });
  } else {
    excursionFacts.push({ heading: labels.bookableFalseLabel, text: labels.bookableFalseText });
  }

  const getUspIconPath = (key) => {
    switch (key) {
      case 'CULTURE_HISTORY':
        return TIME_LOCATION_MUSEUM;
      case 'FOOD':
        return FOOD_BEVERAGE_RESTAURANT;
      case 'BREAKFAST':
        return FOOD_BEVERAGE_BREAKFAST;
      case 'BEVERAGE':
        return FOOD_BEVERAGE_GLASS;
      case 'BEVERAGE_TASTING':
        return FOOD_BEVERAGE_GLASS;
      case 'WINE_TASTING':
        return FOOD_BEVERAGE_GLASS;
      case 'FOOD_AND_BEVERAGE':
        return FOOD_BEVERAGE_MEAL_PACKAGE;
      case 'FAMILY_FRIENDLY':
        return MISC_FAMILY_FRIENDLY;
      case 'GUIDE_SCANDINAVIAN':
        return MISC_FLAG_SCANDINAVIA;
      case 'GUIDE_FI':
        return MISC_FLAG_FI;
      case 'NATURE_ADVENTURE':
        return TRAVEL_MOUNTAIN;
      case 'NATURE_EXPERIENCE':
        return TRAVEL_MOUNTAIN;
      case 'WATER_SPORTS':
        return ACTIVITY_WATER;
      case 'BOAT_TRIP':
        return TRAVEL_SMALL_BOAT;
      case 'SUN_BEACH':
        return ACTIVITY_SUN_BEDS;
      case 'HIKING':
        return ACTIVITY_WALK_HIKE;
      case 'SNORKLING':
        return ACTIVITY_DIVING;
      case 'ISLAND_TOUR':
        return ACTIVITY_ISLAND;
      case 'SAILING':
        return ACTIVITY_SAILBOAT;
      case 'THEME_PARKS':
        return ACTIVITY_THEMEPARK;
      case 'OFFROAD_ADVENTURE':
        return MISC_SPEED;
      default:
        return USER_INTERFACE_TICK_DONE;
    }
  };

  const renderUsps = () => {
    return uspListFiltered?.length > 0 && (
      <div className="gw-excursion-key-selling-points">
        <h3 className="gw-excursion-key-selling-points-header">{labels.uspListHeader}</h3>
        {uspListFiltered.map((usp) => {
          return (
            <div className="gw-excursion-key-selling-point" key={uniqid('usp-')}>
              <div className="gw-excursion-key-selling-point__circle">
                <Icon
                  path={getUspIconPath(usp.key)}
                  className="gw-excursion-key-selling-point__icon"
                />
              </div>
              <p className="gw-excursion-key-selling-point__text">{usp.text}</p>
            </div>
          );
        })}
      </div>
    );
  };

  const renderEsgCriteria = () => {
    if (!labels.esgCriteria || !excursion.esg) {
      return null;
    };
    return (
      <ul className="gw-excursion-esg__criteria-list">
        {Object.keys(labels.esgCriteria).map((key) => (
          excursion.esg[key] && (
            <li className="gw-excursion-esg__criteria-list-item" key={key}>
              <Icon
                className="gw-excursion-esg__checkmark"
                path={USER_INTERFACE_TICK_DONE}
                size="xs"
                sizeMobile="xs"
              />
              <span>{labels.esgCriteria[key]}</span>
            </li>
          )
        ))}
      </ul>
    );
  };
  
  const renderEsgInfo = () => {
    if (!excursion.esg?.esgTravelWithCare) {
      return null;
    };
    return (
      <div className="gw-excursion-esg">
        <EsgLogo className="gw-excursion-esg__logo" esgType="excursion" lazy />
        {labels.esgHeader && (
          <h3 className="gw-excursion-esg__heading">{labels.esgHeader}</h3>
        )}
        {labels.esgBody && (
          <p>{labels.esgBody}</p>
        )}
        {renderEsgCriteria()}
        {labels.esgReadMoreLink?.title && labels.esgReadMoreLink?.href?.url && (
          <ArrowLink
            href={labels.esgReadMoreLink.href.url}
            target={labels.esgReadMoreLink.href.target}
            title={labels.esgReadMoreLink.href.name ?? null}
          >
            {labels.esgReadMoreLink.title}
          </ArrowLink>
        )}       
      </div>
    )
  };

  const renderResortCard = () => {
    const { name, geographical, resortUrl, image } = resort;

    return (
      <div className="gw-excursion-linked-resort">
        <h3 className="gw-excursion-linked-resort__heading">{labels.resortCardHeader}</h3>
        <div className="gw-excursion-resort-card uikit-box--without-padding">
          <div className="gw-excursion-resort-card__image-container">
            <CloudinaryPicture
              className="gw-excursion-resort-card__image"
              img={image}
              sizes={image?.sizes}
              alt={image?.alt || name}
              lazy
              retina
            />
          </div>
          <div className="gw-excursion-resort-card__content">
            <div className="gw-excursion-resort-card__content__left">
              <div className="gw-excursion-resort-card__heading">{name}</div>
              <Subtitle
                geographical={geographical}
                shouldShowRegion={shouldShowRegion(geographical.region.id)}
                className="gw-excursion-resort-card__description"
              />
            </div>
            <Button variant="primary" className="gw-excursion-resort-card__button" href={resortUrl}>
              {labels.resortCardButtonText}
            </Button>
          </div>
        </div>
      </div>
    );
  };

const parseTextWithLinks = (text) => {
  // matches all (global) '{link', value in single quotes after 'type=', value in single quotes after 'url=', and the value between '}' and '{/link}'
  const linkRegex = /{link\s+type=([^}]+)\s+url='([^}]+)'}([^}]+){\/link}/g;

  const textsWithLinks = [];
  let lastIndex = 0;
  let match;

  // eslint-disable-next-line no-cond-assign
  while ((match = linkRegex.exec(text)) !== null) {
    const [_fullMatch, linkType, url, linkText] = match;
    const textSlice = text.slice(lastIndex, match.index);

    textsWithLinks.push({
      text: textSlice,
      linkType,
      linkText: linkText.trim(),
      url: url.trim(),
    });

    lastIndex = linkRegex.lastIndex;
  }

  // Add remaining text after the last link
  if (lastIndex < text.length) {
    const textSlice = text.slice(lastIndex);
    textsWithLinks.push({
      text: textSlice.trim(),
      linkText: '',
      url: '',
    });
  }

  return textsWithLinks;
};

  return (
    <div className={cx("gw-excursion-view", { "uikit-box": !isAppWebView })}>
      <h1 className="gw-excursion-heading">{excursion.heading}</h1>
      <main className="gw-excursion-main-content">
        <div className="gw-excursion-ingress">{excursion.excursionShortDescription}</div>
        <div className="gw-excursion-facts">
          <ul>
            <h3 className="gw-excursion-key-selling-points-header">{labels.overviewHeader}</h3>
            {excursionFacts.map((ef) => {       
              const excursionText = ef.text ? parseTextWithLinks(ef.text) : [];
              return (
                <li
                  key={uniqid('excursion-facts-')}
                  className="gw-excursion-fact"
                >
                  <span className="gw-excursion-fact__heading">
                    {ef.heading}
                    :
                  </span>
                  {excursionText.length && (
                    <>
                      <br />
                        {
                          excursionText.map((textPart) => {
                            return (
                              <div className="gw-excursion-fact__text" key={uniqid('excursion-facts-text-')}>
                              <div className="gw-excursion-fact__text" dangerouslySetInnerHTML={{ __html: textPart.text }} />
                              {
                                textPart.url && <Link className="gw-excursion-fact__link" href={textPart.url}>{textPart.linkText}</Link>
                              }
                              </div>
                            )
                          })
                        }
                    </>
                  )}
                  {ef.moreText && (
                    <>
                      <br />
                      {ef.moreText}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="gw-excursion-description-container">
          { labels.infoHeader && (
            <h3 className="gw-excursion-description-header">{labels.infoHeader}</h3>
          )}
          <div className="gw-excursion-description" dangerouslySetInnerHTML={{ __html: excursion.description }} />
        </div>
      </main>

      <aside className="gw-excursion-sub-content">
        { renderUsps() }
        
        { renderEsgInfo() }

        { resort && (
          renderResortCard()
        )}
      </aside>
    </div>
  );
};

ExcursionInfoView.propTypes = {
  excursion: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    preBookable: PropTypes.bool,
    heading: PropTypes.string,
    description: PropTypes.string,
    shortDescription: PropTypes.string,
    excursionShortDescription: PropTypes.string,
    includedInPrice: PropTypes.string,
    foodAndBeverage: PropTypes.string,
    goodToBring: PropTypes.string,
    additionalInfo: PropTypes.string,
    otherBookingInfo: PropTypes.string,
    imageUrl: PropTypes.string,
    imageText: PropTypes.string,
    timeDuration: PropTypes.string,
    guide: PropTypes.string,
    guideFI: PropTypes.string,
    day: PropTypes.string,
    dayFI: PropTypes.string,
    priceAdult: PropTypes.string,
    priceAdultFI: PropTypes.string,
    priceChild: PropTypes.string,
    priceChildFI: PropTypes.string,
    currency: PropTypes.string,
    uspList: PropTypes.arrayOf(PropTypes.shape({})),
    ageRestriction: PropTypes.string,
    esg: PropTypes.objectOf(PropTypes.bool),
  }).isRequired,
  resort: PropTypes.shape({
    name: PropTypes.string.isRequired,
    geographical: PropTypes.shape(),
    resortUrl: PropTypes.string.isRequired,
    image: PropTypes.shape({
      cloudinaryUriTemplate: PropTypes.string.isRequired,
      alt: PropTypes.string,
      sizes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
  }),
  labels: PropTypes.shape({
    overviewHeader: PropTypes.string,
    dayLabel: PropTypes.string,
    durationLabel: PropTypes.string,
    priceLabel: PropTypes.string,
    perAdultLabel: PropTypes.string,
    perChildLabel: PropTypes.string,
    guideLabel: PropTypes.string,
    ageRestrictionLabel: PropTypes.string,
    ageRestrictionText: PropTypes.string,
    goodToBringLabel: PropTypes.string,
    otherLabel: PropTypes.string,
    bookableTrueLabel: PropTypes.string,
    bookableTrueText: PropTypes.string,
    bookableFalseLabel: PropTypes.string,
    bookableFalseText: PropTypes.string,
    uspListHeader: PropTypes.string,
    infoHeader: PropTypes.string,
    resortCardHeader: PropTypes.string,
    resortCardButtonText: PropTypes.string,
    esgCriteria: PropTypes.objectOf(PropTypes.string),
    esgHeader: PropTypes.string,
    esgBody: PropTypes.string,
    esgReadMoreLink: PropTypes.shape({
      title: PropTypes.string,
      href: PropTypes.shape({
        url: PropTypes.string,
        name: PropTypes.string,
        target: PropTypes.string,
      }),
    }),
  }).isRequired,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isAppWebView: PropTypes.bool,
};

ExcursionInfoView.defaultProps = {
  resort: null,
  isAppWebView: false,
};

export default ExcursionInfoView;
